import { browserTimeZoneQuery } from "../timeZone";
import { PhotoImage } from "../components/PhotoImage";
import { AskTextInput } from "../components/AskTextInput";
/**
 * Photos — every image a walk captured, newest first, in day sections.
 *
 * The console had no photos surface. Media reached a reader only through the
 * walk it came from or the record it was attached to, so "that cracked slab
 * picture from last week" meant remembering which walk it was on. This is the
 * list that answers it directly.
 *
 * ## Search spans the whole scoped library
 *
 * Text, project, kind and capture-date filters run on the server before
 * pagination. Typing is debounced; Enter remains the explicit Ask Led AI
 * action. Only the returned page is transferred and presigned.
 *
 * ## Paging matches the item list's rule
 *
 * The server caps the read and says whether it capped. Asking for more raises
 * the LIMIT rather than passing an offset: this list is newest-first, and a
 * walk finishing mid-scroll would shift every offset under the reader.
 */

import { useEffect, useRef, useState } from "react";
import {
  fetchPhotos,
  type ApiError,
  type ConsolePhotoDayResponse,
  type ConsolePhotoResponse,
  type PhotosResponse,
} from "../api";
import { DateRangePicker, type DayRange } from "../components/DateRangePicker";
import { PhotoFullscreenViewer, type FullscreenPhoto } from "../components/PhotoFullscreenViewer";
import { AnchoredPanel } from "../components/AnchoredPanel";
import { copyToClipboard } from "../lib/clipboard";
import { downloadFile, photoFileUrl } from "../lib/photoTransfer";
import { consoleUrl } from "../basePath";
import { useResource } from "../useResource";
import { resourceQuery } from "../resourceCache";
import { C, FONT_MONO } from "../tokens";
import { PHOTO_TILE_MIN } from "../layout";
import {
  ConsoleViewProps,
  EmptyPanel,
  FailurePanel,
  LoadingPanel,
  META,
  MONTHS,
  dateWithYear,
  PAGE_STEP,
  ROW_TITLE,
  StaleBanner,
  ViewBody,
  ViewHeader,
  hrefWithAll,
  plural,
  scopedSearch,
} from "../components/chrome";

/**
 * The date filter in effect, read from the URL rather than held in state.
 *
 * The URL is the source of truth so a filtered gallery is a link — and so
 * `useResource` re-fetches on it, which is required rather than optional: the
 * range is applied by the SERVER (see `listPhotos`), because this read is
 * capped at the newest page of visits and a browser-side filter for March
 * would search April and find nothing.
 *
 * Both ends or neither. A half-written range is a 400 from the route, so a
 * URL carrying one is treated as carrying none instead of rendering a page
 * that will fail to load.
 */
function rangeFrom(search: string): DayRange | null {
  const at = search.indexOf("?");
  const raw = at >= 0 ? search.slice(at + 1) : search.startsWith("/") ? "" : search;
  const params = new URLSearchParams(raw);
  const from = params.get("from");
  const to = params.get("to");
  const day = /^\d{4}-\d{2}-\d{2}$/;
  if (!from || !to || !day.test(from) || !day.test(to)) return null;
  return from <= to ? { from, to } : { from: to, to: from };
}

/**
 * The read's query: whatever the console's search carries, plus `limit`, with
 * the date range NORMALIZED to what the route will accept.
 *
 * The normalizing half is not cosmetic. The route rejects half a range with a
 * 400 — deliberately, so a filter can never be silently ignored — and this
 * function is what forwards the URL to it. Passing a lone `from` through
 * would therefore turn a hand-edited or half-truncated link into a FAILURE
 * page, while the pill above it read "Any time". The gallery and the request
 * have to agree about what is being asked, so both go through `rangeFrom`.
 */
function photosQuery(search: string, limit: number, query: string, filters: PhotoFilters): string {
  const at = search.indexOf("?");
  const params = new URLSearchParams(at >= 0 ? search.slice(at + 1) : search.replace(/^\?/, ""));
  params.set("limit", String(limit));
  params.delete("q");
  params.delete("kind");
  params.delete("photoSite");
  // The gallery API accepts a short search phrase. Keep its existing bound
  // without limiting the editable draft or the full question handed to Ask.
  if (query) params.set("q", query.slice(0, 200));
  if (filters.kind !== "any") params.set("kind", filters.kind);
  if (filters.site) params.set("photoSite", filters.site);
  const range = rangeFrom(search);
  if (range) {
    params.set("from", range.from);
    params.set("to", range.to);
  } else {
    params.delete("from");
    params.delete("to");
  }
  return resourceQuery(`?${params}`, ["workspace", "site", "from", "to", "limit", "q", "kind", "photoSite"]);
}

/**
 * The grid's tile: one size, every tile.
 *
 * This was a mosaic — 150px cells with roughly one tile in six spanning two
 * of them. The argument for it was that a wall of site photos is mostly
 * repetition and the ones given room read as the ones worth looking at. That
 * argument is fine; the implementation could not honour it. The span came
 * from a hash of the photo's id, so the tiles it enlarged were not the
 * important ones — they were the ones whose id happened to hash low. It
 * dressed noise up as emphasis, and the ragged edges it left made the wall
 * harder to scan rather than easier.
 *
 * A contact sheet is the right object, and a contact sheet has one tile size.
 * 4:3 rather than a fixed height, so a tile keeps its shape at every column
 * count the page reflows through.
 */
const TILE_MIN = PHOTO_TILE_MIN;
const TILE_GAP = 10;

/**
 * One photo's identity, for the selection set.
 *
 * `id` alone is not it. A media item's id is unique within its VISIT, and
 * this page flattens many visits into one wall, so a bare id can name two
 * different photographs. The grid already keys its children this way; the
 * selection has to agree with it or a tick lands on the wrong tile.
 */
export function photoKey(photo: { id: string; visitId: string }): string {
  return `${photo.visitId}:${photo.id}`;
}

/** The queries the empty box offers. Real ones: each is text the search
 *  can actually match against a description, so pressing one narrows the
 *  gallery rather than teaching the reader a syntax that does nothing. */
const PHOTO_SUGGESTIONS = ["rebar", "concrete", "safety", "roof"];


// ---------------------------------------------------------------------------
// The filter chips — the design's `photoFilters`
// ---------------------------------------------------------------------------

/** Project and kind narrow the server query alongside the date range. */
type PhotoKind = "any" | "image" | "video";

export interface PhotoFilters {
  site: string | null;
  kind: PhotoKind;
}

export const NO_PHOTO_FILTERS: PhotoFilters = { site: null, kind: "any" };

export function photoFiltersActive(filters: PhotoFilters): boolean {
  return filters.site !== null || filters.kind !== "any";
}

/** Project/kind options have no page-local counts: unloaded matches count too. */
function FilterChip({
  label,
  value,
  active,
  options,
  onPick,
}: {
  label: string;
  value: string;
  active: boolean;
  options: Array<{ id: string; label: string }>;
  onPick: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement | null>(null);
  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="kb"
        data-testid="photos-filter"
        data-filter={label.toLowerCase()}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          height: 34,
          padding: "0 10px 0 12px",
          borderRadius: 9,
          background: active ? C.surface : "transparent",
          border: `1px solid ${active ? C.line : "transparent"}`,
          fontSize: 14.95,
          fontWeight: 500,
          color: C.ink,
          whiteSpace: "nowrap",
          cursor: "pointer",
        }}
      >
        <span style={{ color: C.sub2, fontWeight: 500 }}>{label}</span>
        {value}
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden>
          <path
            d="M1.5 2l4.5 4 4.5-4"
            stroke={C.sub2}
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <AnchoredPanel
          anchorRef={anchor}
          label={label}
          testId="photos-filter-menu"
          minWidth={200}
          onClose={() => setOpen(false)}
        >
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              role="option"
              aria-selected={option.label === value}
              className="kb navrow"
              data-testid="photos-filter-option"
              onClick={() => {
                setOpen(false);
                onPick(option.id);
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 10,
                height: 36,
                padding: "0 10px",
                borderRadius: 8,
                textAlign: "left",
                fontSize: 15.52,
                fontWeight: 500,
                background: option.label === value ? C.faint : "transparent",
              }}
            >
              <span style={{ ...ROW_TITLE, flex: 1 }}>{option.label}</span>
            </button>
          ))}
        </AnchoredPanel>
      )}
    </>
  );
}

export function Photos(props: ConsoleViewProps) {
  const {
    search,
    refreshKey,
    personalHref,
    onGoPersonal,
    onNavigate,
    hrefForWalk,
  } = props;

  const range = rangeFrom(search);
  /** Writing the range navigates: `from` and `to` move together, and every
   *  other parameter — `workspace` above all — rides along. */
  const hrefForRange = (next: DayRange | null) =>
    hrefWithAll(search, [
      ["from", next ? next.from : null],
      ["to", next ? next.to : null],
    ]);

  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<PhotoFilters>(NO_PHOTO_FILTERS);
  const [saving, setSaving] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const needle = query.trim().toLowerCase();
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    if (needle === debouncedQuery) return;
    const timer = setTimeout(() => setDebouncedQuery(needle), needle ? 250 : 0);
    return () => clearTimeout(timer);
  }, [needle, debouncedQuery]);
  // Key the page window to its criteria so a new search starts at one page
  // immediately, without first issuing an oversized request for the old limit.
  const dataSearch = scopedSearch(search, props.scopeSiteId);
  const criteria = photosQuery(dataSearch, PAGE_STEP, debouncedQuery, filters);
  const [pagination, setPagination] = useState({ criteria, limit: PAGE_STEP });
  const limit = pagination.criteria === criteria ? pagination.limit : PAGE_STEP;
  useEffect(() => {
    setPagination((current) => current.criteria === criteria ? current : { criteria, limit: PAGE_STEP });
  }, [criteria]);
  const request = photosQuery(dataSearch, limit, debouncedQuery, filters);
  /** The small target after the last day. Seeing it asks for the next page,
   *  like an infinite product list, rather than making the reader hunt for a
   *  pagination button. */
  const morePhotosRef = useRef<HTMLDivElement>(null);
  const loadingOlderRef = useRef(false);
  const [viewer, setViewer] = useState<number | null>(null);
  /**
   * Selection mode, and what is picked in it.
   *
   * Held as a set of `photoKey`s rather than as a flag on each photo,
   * because `data` is re-fetched on every refresh and a flag written onto a
   * row would be thrown away with it. A key survives the row.
   */
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  /** Whether the last Copy links reached the clipboard, and what to say
   *  about it. Both reset on the next change to the selection, so the
   *  confirmation never outlives the thing it confirmed. */
  const [copied, setCopied] = useState<"idle" | "done" | "failed">("idle");
  const [copiedNote, setCopiedNote] = useState("");

  const { data: response, failure, loading, refreshFailure } = useResource<{
    criteria: string;
    request: string;
    photos: PhotosResponse;
  }>(
    `photos:${request}&${browserTimeZoneQuery()}`,
    async () => ({ criteria, request, photos: await fetchPhotos(request) }),
    refreshKey,
    // Keep the search box mounted and keep tiles in place when extending a
    // page. Changed criteria display a loading state until their own response.
    { keepPreviousDataOnNavigation: true },
  );
  const data = response?.photos;
  const searching = needle !== debouncedQuery || response?.criteria !== criteria;
  const narrowed = needle !== "" || photoFiltersActive(filters);
  const canLoadMore = !!data?.hasMore && !searching && !refreshFailure;

  // One observer per visible continuation target. The ref gates callbacks
  // until the request settles: browsers can report the same intersection
  // more than once before React has painted `loading`, and every report must
  // still mean one page, not several skipped pages.
  useEffect(() => {
    // While a larger LIMIT is scheduling, `useResource` still exposes the
    // prior response for one render. Its `loading` value can therefore still
    // be false; matching the response's request proves that this request, rather
    // than the previous page, has actually settled.
    if (!loading && response?.request === request) loadingOlderRef.current = false;
  }, [response?.request, request, loading]);

  useEffect(() => {
    const target = morePhotosRef.current;
    if (!target || !canLoadMore || loading || response?.request !== request || typeof IntersectionObserver === "undefined") {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || loadingOlderRef.current) return;
        loadingOlderRef.current = true;
        setPagination({ criteria, limit: limit + PAGE_STEP });
      },
      // Start the request before the reader reaches a hard stop, while still
      // requiring that they have deliberately scrolled into the final page.
      { rootMargin: "0px 0px 320px", threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [canLoadMore, loading, limit, criteria, request, response?.request]);
  const days = searching ? [] : data?.days ?? [];

  /** Escape leaves selection mode, the way it leaves every other mode in
   *  this console. Only bound while the mode is on, so it cannot swallow an
   *  Escape the fullscreen viewer wanted — and the viewer cannot be open
   *  here anyway, since a tile click ticks instead of opening. */
  useEffect(() => {
    if (!selectMode) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setSelectMode(false);
      setSelected(new Set());
      setCopied("idle");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectMode]);

  if (failure) {
    return (
      <FailurePanel
        failure={failure as ApiError}
        personalHref={personalHref}
        onGoPersonal={onGoPersonal}
      />
    );
  }
  if (!data) return <LoadingPanel shape="photos" />;

  const shown = days.reduce((sum, day) => sum + day.photos.length, 0);
  const galleryPhotos = days.flatMap((day) => day.photos);
  // The row as the server sent it, `location` included. It used to also set a
  // `fileName` — from the DESCRIPTION — which the viewer then printed under a
  // label claiming it was a file name, so the same sentence appeared twice
  // (user, 2026-09-10).
  const viewerPhotos: FullscreenPhoto[] = galleryPhotos;
  // Fetch through our own server: storage URLs are cross-origin and cannot
  // force a download. The server returns one original or one selection ZIP.
  const downloadPhotos = async (photos: ConsolePhotoResponse[]) => {
    if (saving || photos.length === 0) return;
    setSaving(true);
    setDownloadFailed(false);
    const ok = await downloadFile(`${consoleUrl("/api/photos/download")}${request}`, "photos.zip", {
      authenticated: true,
      body: JSON.stringify({ photos: photos.map((photo) => ({ visitId: photo.visitId, photoId: photo.id })) }),
    });
    setDownloadFailed(!ok);
    setSaving(false);
  };

  // -------------------------------------------------------------------------
  // Selection — the design's PHOTOS screen, `selectionVals`
  // -------------------------------------------------------------------------

  /**
   * What is picked AND on screen.
   *
   * The set can outlive a filter: tick four photos, then narrow to one
   * project, and three of them are no longer rendered. Every count and every
   * action reads this list rather than the raw set, so the bar can never
   * offer to download a photograph the reader cannot see — and clearing the
   * filter brings the rest of the selection back rather than having silently
   * dropped it.
   */
  const selectedShown = galleryPhotos.filter((photo) => selected.has(photoKey(photo)));
  const selectedCount = selectedShown.length;
  const allShownSelected = shown > 0 && selectedCount === shown;

  const setSelection = (next: ReadonlySet<string>) => {
    setSelected(next);
    setCopied("idle");
    setCopiedNote("");
  };
  const toggleOne = (photo: ConsolePhotoResponse) => {
    const key = photoKey(photo);
    const next = new Set(selected);
    if (!next.delete(key)) next.add(key);
    setSelection(next);
  };
  /** Every photo of one day, on or off together — the design's day-header
   *  control. Off when the day is already whole, on otherwise, which is what
   *  makes a part-selected day fill rather than empty. */
  const toggleDay = (photos: ConsolePhotoResponse[], on: boolean) => {
    const next = new Set(selected);
    for (const photo of photos) {
      if (on) next.add(photoKey(photo));
      else next.delete(photoKey(photo));
    }
    setSelection(next);
  };
  const toggleSelectMode = () => {
    setSelectMode((current) => !current);
    setSelected(new Set());
    setCopied("idle");
  };

  /**
   * Copy links — to the WALKS, not to the images.
   *
   * The tempting reading is the image URLs, and it is the wrong one: those
   * are presigned R2 GETs that expire in fifteen minutes (`media.ts`), so a
   * pasted one is dead before the email it was pasted into is answered. A
   * link that stops working is worse than no link.
   *
   * A photograph's durable address in this console is the walk it was taken
   * on, which is also the page that shows it in context. Several photos from
   * one walk are therefore ONE link, and the confirmation says so — the
   * reader picked six and got two lines, and a count that did not mention it
   * would read as a bug.
   */
  const copyLinks = async () => {
    if (selectedCount === 0) return;
    const origin = typeof location === "undefined" ? "" : location.origin;
    const links: string[] = [];
    const seen = new Set<string>();
    for (const photo of selectedShown) {
      if (seen.has(photo.visitId)) continue;
      seen.add(photo.visitId);
      links.push(`${origin}${hrefForWalk(photo.visitId)}`);
    }
    const ok = await copyToClipboard(links.join("\n"));
    setCopied(ok ? "done" : "failed");
    setCopiedNote(
      ok
        ? links.length === selectedCount
          ? `${plural(links.length, "link", "links")} copied`
          : `${plural(links.length, "walk link", "walk links")} copied for ${selectedCount} photos`
        : "Could not copy",
    );
  };
  const askHref = `?${new URLSearchParams({
    ...Object.fromEntries(
      new URLSearchParams(search.startsWith("?") ? search.slice(1) : search),
    ),
    page: "ask",
    ask: query.trim(),
  }).toString()}`;

  return (
    <ViewBody
      // Wider than the console's default column. The gallery is the one page
      // whose content IS the width: an extra track of photographs is an extra
      // photograph seen, where on a list it is only a longer line.
      wide="record"
      header={
        <ViewHeader
          wide="record"
          crumb={
            data.activeWorkspace.type === "personal"
              ? "Personal"
              : data.activeWorkspace.name || "Team"
          }
          count={searching ? undefined : plural(data.total ?? data.count, "photo", "photos")}
          title="Photos"
          note={searching ? "Searching photos…" : data.hasMore
            ? `${data.count} loaded · scroll down to load older photos` : undefined}
          actions={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* The design's Select toggle. It reads `Done` once on, because
                the way out of a mode should be the control that entered it —
                the ✕ on the bar is the same exit, within reach of the
                selection rather than of the page title. */}
            <button
              type="button"
              className="kb"
              data-testid="photos-select-toggle"
              aria-pressed={selectMode}
              disabled={!selectMode && data.count === 0}
              onClick={toggleSelectMode}
              style={{
                flex: "none",
                display: "flex",
                alignItems: "center",
                gap: 7,
                height: 40,
                padding: "0 16px",
                borderRadius: 10,
                border: `1px solid ${selectMode ? C.ink : C.line2}`,
                background: selectMode ? C.ink : C.surface,
                color: selectMode ? C.selBarOn : C.ink,
                fontSize: 15.52,
                fontWeight: 600,
                whiteSpace: "nowrap",
                cursor: !selectMode && data.count === 0 ? "default" : "pointer",
                opacity: !selectMode && data.count === 0 ? 0.5 : 1,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M4 12.5l5 5L20 6"
                  stroke={selectMode ? C.selBarOn : C.sub}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {selectMode ? "Done" : "Select"}
            </button>
            </div>
          }
        >
          {/* `ViewHeader` pads its top and not its bottom — most pages end
              their header on text, whose line-height leaves the gap. This one
              ends on a 58px bar with a shadow, which would land flush on the
              first day card. The clearance belongs here, on the child that is
              unlike the others, rather than in `ViewHeader` where it would
              move every page to fix one. */}
          <div
            style={{
              marginTop: 16,
              marginBottom: 22,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {/* The design's bar: 58px, radius 18, and a placeholder that
                SHOWS what the box takes rather than naming it. "Filter these
                photos" is a label; "exposed rebar", "the gate", "last
                Tuesday" is an example, and an example is the only thing that
                tells a reader they may type a date. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                minHeight: 58,
                padding: "8px 9px 8px 20px",
                borderRadius: 18,
                background: C.surface,
                border: `1px solid ${needle ? C.line2 : C.line}`,
                boxShadow: `0 1px 2px ${C.softShadow}, 0 6px 18px ${C.softShadowLow}`,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle cx="11" cy="11" r="7" stroke={C.sub2} strokeWidth="1.9" />
                <path
                  d="M16.5 16.5 21 21"
                  stroke={C.sub2}
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
              </svg>
              <div style={{ flex: 1, minWidth: 0 }}>
                <AskTextInput
                  parts={[{ text: query, selected: false }]}
                  data-testid="photos-search"
                  aria-label="Search all photos, or press Enter to ask Led AI"
                  value={query}
                  placeholder="Search all photos by description, walk, or project"
                  onInput={(event) => setQuery(event.currentTarget.value)}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" || !query.trim()) return;
                    event.preventDefault();
                    onNavigate(askHref);
                  }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    color: C.ink,
                    fontFamily: "inherit",
                    fontSize: 18.4,
                  }}
                />
              </div>
              {query !== "" && (
                <button
                  type="button"
                  className="kb"
                  data-testid="photos-clear"
                  aria-label="Clear the search"
                  title="Clear the search"
                  onClick={() => setQuery("")}
                  style={{
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    border: "none",
                    background: C.hairRow,
                    color: C.sub,
                    cursor: "pointer",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M6 6l12 12M18 6L6 18"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              )}
              <button
                type="button"
                className="kb"
                data-testid="photos-ask"
                aria-label="Ask Led AI"
                title="Ask Led AI"
                disabled={!query.trim()}
                onClick={() => onNavigate(askHref)}
                style={{
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  padding: 0,
                  borderRadius: 999,
                  border: "none",
                  background: query.trim() ? C.ink : C.faint,
                  color: query.trim() ? C.surface : C.sub2,
                  cursor: query.trim() ? "pointer" : "default",
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              </button>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
                padding: "0 2px",
              }}
            >
              <FilterChip
                label="Project"
                value={
                  filters.site
                    ? data.sites.find((site) => site.id === filters.site)?.name ?? "One project"
                    : "All"
                }
                active={filters.site !== null}
                options={[
                  { id: "", label: "All" },
                  ...data.sites.map((site) => ({
                    id: site.id,
                    label: site.name || site.id,
                  })),
                ]}
                onPick={(id) =>
                  setFilters((current) => ({ ...current, site: id === "" ? null : id }))
                }
              />
              <DateRangePicker
                testId="photos-range"
                label="Filter photos by date"
                value={range}
                onChange={(next) => onNavigate(hrefForRange(next))}
              />
              <FilterChip
                label="Kind"
                value={
                  filters.kind === "any" ? "Any" : filters.kind === "video" ? "Video" : "Photos"
                }
                active={filters.kind !== "any"}
                options={[
                  { id: "any", label: "Any" },
                  { id: "image", label: "Photos" },
                  { id: "video", label: "Video" },
                ]}
                onPick={(id) => setFilters((current) => ({ ...current, kind: id as PhotoKind }))}
              />
              {photoFiltersActive(filters) && (
                <button
                  type="button"
                  className="kb"
                  data-testid="photos-filters-clear"
                  onClick={() => setFilters(NO_PHOTO_FILTERS)}
                  style={{
                    height: 34,
                    padding: "0 10px",
                    border: "none",
                    background: "transparent",
                    fontSize: 14.95,
                    fontWeight: 500,
                    color: C.sub,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              )}

              {/* The narrowing says what it did. A filter that shortens a list
                  without saying by how much leaves the reader unable to tell
                  an empty result from a broken one. */}
              {narrowed && (
                <span
                  data-testid="photos-filter-note"
                  style={{ ...META, fontSize: 10.35, color: C.sub2 }}
                >
                  {searching ? "Searching all photos…" : `${shown} of ${plural(data.total ?? data.count, "matching photo", "matching photos")}${
                    needle !== "" ? " · Enter asks Led AI" : ""
                  }`}
                </span>
              )}
              <span style={{ flex: 1 }} />
              {/* The design's `Try` row. Only offered when the box is empty:
                  a suggestion beside a query the reader is already typing
                  reads as a correction. */}
              {needle === "" && (
                <>
                  <span style={{ ...META, fontSize: 10.35, color: C.sub2 }}>Try</span>
                  {PHOTO_SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      className="kb"
                      data-testid="photos-suggestion"
                      onClick={() => setQuery(suggestion)}
                      style={{
                        height: 30,
                        padding: "0 12px",
                        borderRadius: 999,
                        background: C.surface,
                        border: `1px solid ${C.hair}`,
                        color: C.ink,
                        fontSize: 14.95,
                        fontWeight: 500,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>
        </ViewHeader>
      }
    >
      {refreshFailure && <StaleBanner failure={refreshFailure} />}
      {downloadFailed && (
        <p role="alert" style={{ color: C.sub }}>
          Download failed. Please try again or select fewer photos.
        </p>
      )}

      {searching ? (
        refreshFailure ? <FailurePanel failure={refreshFailure} personalHref={personalHref} onGoPersonal={onGoPersonal} />
          : <LoadingPanel shape="photos" />
      ) : data.count === 0 && narrowed ? (
        <EmptyPanel
          title="No photo matches that"
          body="No photos in this scope match your search and filters. Try another description, widen the filters, or press Enter to ask Led AI."
        />
      ) : data.count === 0 && range ? (
        /* "No photos in this team yet" would be a lie with a range on — the
           team may have thousands, just none in the days asked for. And the
           way out is named, because a reader who filtered to the wrong week
           should not have to work out that the pill above is why the page is
           empty. */
        <EmptyPanel
          title="No photos in those days"
          body="Nothing was captured in the range you picked. Widen it, or clear the filter to see everything."
          action={{
            label: "Clear the date filter",
            href: hrefForRange(null),
            onClick: () => onNavigate(hrefForRange(null)),
          }}
        />
      ) : data.count === 0 ? (
        <EmptyPanel
          title={`No photos in ${
            data.activeWorkspace.type === "personal" ? "Personal" : "this team"
          } yet`}
          body="Photos appear here as soon as a walk captures one."
        />
      ) : (
        days.map((day) => (
          <PhotoDayCard
            key={day.day}
            day={day}
            showSite={data.sites.length > 1}
            hrefForWalk={hrefForWalk}
            onNavigate={onNavigate}
            selectMode={selectMode}
            selected={selected}
            anySelected={selectedCount > 0}
            onToggleOne={toggleOne}
            onToggleDay={toggleDay}
            onView={(photo) =>
              setViewer(
                galleryPhotos.findIndex(
                  (candidate) =>
                    candidate.id === photo.id && candidate.visitId === photo.visitId,
                ),
              )
            }
          />
        ))
      )}

      {/* Search results use the same paginated scroll as the full library. */}
      {canLoadMore && (
        <div
          ref={morePhotosRef}
          data-testid="photos-more"
          role="status"
          aria-live="polite"
          aria-label="More photos load automatically as you scroll"
          style={{
            display: "flex",
            justifyContent: "center",
            minHeight: 28,
            padding: "10px 4px",
            color: C.sub,
            ...META,
            fontSize: 10.35,
          }}
        >
          {loading ? "Loading older photos…" : null}
        </div>
      )}

      {/*
        The design's floating action bar: fixed to the foot of the window,
        centred, above everything. It is fixed rather than pinned to the
        gallery because the selection is made by scrolling — a bar that
        scrolled away with the first day would leave the reader holding
        twenty ticked photos and no way to act on them.

        The wrapper takes no pointer events so the full-width strip does not
        eat clicks on the gallery behind it; the bar itself takes them back.
      */}
      {selectMode && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 24,
            zIndex: 30,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            padding: "0 24px",
          }}
        >
          <div
            data-testid="photos-select-bar"
            style={{
              pointerEvents: "auto",
              display: "flex",
              alignItems: "center",
              gap: 10,
              height: 56,
              padding: "0 10px 0 18px",
              borderRadius: 16,
              background: C.ink,
              color: C.selBarOn,
              boxShadow: `0 10px 30px ${C.selBarShadow}`,
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <span
              data-testid="photos-select-count"
              style={{ fontSize: 16.1, fontWeight: 600, whiteSpace: "nowrap" }}
            >
              {/* Before anything is ticked the bar says what the mode is FOR
                  rather than "0 selected", which reads as a failed count. */}
              {selectedCount === 0 ? "Select photos" : `${selectedCount} selected`}
            </span>
            <span style={{ width: 1, height: 20, background: C.selBarRule }} />
            <button
              type="button"
              className="kb"
              data-testid="photos-select-all"
              onClick={() =>
                allShownSelected ? setSelection(new Set()) : toggleDay(galleryPhotos, true)
              }
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                fontSize: 14.95,
                fontWeight: 500,
                color: C.selBarQuiet,
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
            >
              {/* Counts what is SHOWN, because that is what it will tick —
                  the same rule downloading the selection follows. */}
              {allShownSelected ? "Deselect all" : `Select all ${shown}`}
            </button>
            <span style={{ flex: 1, minWidth: 8 }} />

            {/* Said out loud, because a copy that silently did nothing is the
                one failure that matters — the rule `lib/clipboard` exists
                for. It also names the WALK links, so six photos coming back
                as two lines reads as the dedupe it is. */}
            {copied !== "idle" && (
              <span
                data-testid="photos-copy-note"
                style={{
                  fontSize: 14.37,
                  fontWeight: 500,
                  color: copied === "done" ? C.selBarQuiet : C.red,
                  whiteSpace: "nowrap",
                }}
              >
                {copiedNote}
              </span>
            )}

            <button
              type="button"
              className="kb"
              data-testid="photos-copy-links"
              disabled={selectedCount === 0}
              onClick={() => void copyLinks()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                height: 38,
                padding: "0 12px",
                borderRadius: 10,
                border: `1px solid ${C.selBarEdge}`,
                background: "transparent",
                color: C.selBarOn,
                fontSize: 14.95,
                fontWeight: 600,
                whiteSpace: "nowrap",
                opacity: selectedCount === 0 ? 0.4 : 1,
                cursor: selectedCount === 0 ? "default" : "pointer",
              }}
            >
              {/* A chain link, where the design draws its `list` glyph —
                  three horizontal rules, which beside "Copy links" reads as
                  "show me a list". The design system has 22 icons and no
                  link among them, so `list` there is what was to hand rather
                  than what was meant. Everything else in this bar is the
                  design's own geometry. */}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M10 14a4.5 4.5 0 0 0 6.4 0l2.6-2.6a4.5 4.5 0 0 0-6.4-6.4L11.5 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M14 10a4.5 4.5 0 0 0-6.4 0L5 12.6a4.5 4.5 0 0 0 6.4 6.4l1.1-1.1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Copy links
            </button>

            <button
              type="button"
              className="kb"
              data-testid="photos-download-selected"
              disabled={selectedCount === 0 || saving}
              onClick={() => void downloadPhotos(selectedShown)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                height: 38,
                padding: "0 14px",
                borderRadius: 10,
                border: "none",
                background: C.gold,
                color: C.onGold,
                fontSize: 14.95,
                fontWeight: 700,
                whiteSpace: "nowrap",
                opacity: selectedCount === 0 ? 0.4 : 1,
                cursor: saving ? "progress" : selectedCount === 0 ? "default" : "pointer",
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M12 4v11M7 11l5 5 5-5M4.5 19.5h15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {saving ? "Preparing download…" : downloadFailed ? "Retry download" : "Download"}
            </button>

            <button
              type="button"
              className="kb"
              data-testid="photos-select-close"
              aria-label="Leave selection mode"
              title="Leave selection mode"
              onClick={toggleSelectMode}
              style={{
                display: "grid",
                placeItems: "center",
                width: 38,
                height: 38,
                borderRadius: 10,
                border: "none",
                background: "transparent",
                color: C.selBarQuiet,
                cursor: "pointer",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M5 5l14 14M19 5L5 19"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {viewer !== null && viewerPhotos[viewer] && (
        <PhotoFullscreenViewer
          photos={viewerPhotos}
          index={viewer}
          downloadUrl={`${photoFileUrl(galleryPhotos[viewer]!.visitId, galleryPhotos[viewer]!.id, null)}${request}`}
          onStep={setViewer}
          onClose={() => setViewer(null)}
        />
      )}
    </ViewBody>
  );
}

/**
 * One day of photographs, as the design's card.
 *
 * The old shape was a bare mono heading over a uniform grid. Two things were
 * wrong with it. The heading was a line of small caps competing with the
 * captions under the tiles it was meant to head — so the page had no
 * structure a reader could scan. And the day said only its date: not how many
 * walks made it, not which ones, so a reader who recognised a photo had no
 * route from it to the walk it came from except by opening it.
 *
 * The card gives the day a block of its own — month over day number, the
 * weekday spelled out — and puts the day's WALKS in the header as chips that
 * go there. Those are derived from the photos themselves, so the header
 * cannot claim a walk the day did not produce.
 */
function PhotoDayCard({
  day,
  showSite,
  hrefForWalk,
  onNavigate,
  selectMode,
  selected,
  anySelected,
  onToggleOne,
  onToggleDay,
  onView,
}: {
  day: ConsolePhotoDayResponse;
  showSite: boolean;
  hrefForWalk: (visitId: string) => string;
  onNavigate: (href: string) => void;
  selectMode: boolean;
  selected: ReadonlySet<string>;
  /** Whether anything at all is picked, anywhere on the page — what dims the
   *  tiles that are not. */
  anySelected: boolean;
  onToggleOne: (photo: ConsolePhotoResponse) => void;
  onToggleDay: (photos: ConsolePhotoResponse[], on: boolean) => void;
  onView: (photo: ConsolePhotoResponse) => void;
}) {
  const parts = dayParts(day.day);
  const walks = walksOf(day.photos);
  const sites = new Set(day.photos.map((photo) => photo.siteName).filter(Boolean));
  const pickedHere = day.photos.filter((photo) => selected.has(photoKey(photo))).length;
  const allHere = day.photos.length > 0 && pickedHere === day.photos.length;
  const someHere = pickedHere > 0 && !allHere;

  return (
    <div
      data-testid="photo-day"
      data-day={day.day}
      style={{
        background: C.surface,
        border: `1px solid ${C.hair}`,
        borderRadius: 16,
        boxShadow: `0 1px 2px ${C.softShadow}`,
        overflow: "hidden",
        marginBottom: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "16px 20px",
          borderBottom: `1px solid ${C.hair}`,
          flexWrap: "wrap",
        }}
      >
        <span
          aria-hidden
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: 46,
            height: 46,
            borderRadius: 11,
            background: C.hairRow,
            flex: "none",
          }}
        >
          <span style={{ ...META, fontSize: 9.77, color: C.sub2, lineHeight: 1 }}>
            {parts.month}
          </span>
          <span
            style={{
              fontSize: 21.85,
              fontWeight: 700,
              letterSpacing: "-0.38px",
              lineHeight: 1,
              marginTop: 3,
            }}
          >
            {parts.dayNum}
          </span>
        </span>

        <span
          style={{
            flex: "1 1 160px",
            minWidth: 160,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <span
            style={{ fontSize: 19.55, fontWeight: 700, letterSpacing: "-0.34px", whiteSpace: "nowrap" }}
          >
            {parts.weekday}
          </span>
          <span style={{ ...META, fontSize: 10.93, color: C.sub2, whiteSpace: "nowrap" }}>
            {[
              // The block beside it says month and day; the year is said
              // here, as on every console date (LED-629).
              dayDate(day.day),
              plural(day.photos.length, "photo", "photos"),
              plural(walks.length, "walk", "walks"),
              showSite && sites.size > 0 ? plural(sites.size, "project", "projects") : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </span>

        {/*
          Whole days on and off together — the design's day-header control.

          A day is the unit a superintendent actually wants ("send Thursday
          to the architect"), and ticking eleven tiles to get one is the work
          this removes. The box carries three states, not two: a bar rather
          than a tick when the day is PART selected, so a reader can see at
          the header that some of it is picked without counting tiles.
        */}
        {selectMode && (
          <button
            type="button"
            className="kb"
            data-testid="photo-day-select-all"
            aria-pressed={allHere}
            onClick={() => onToggleDay(day.photos, !allHere)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              height: 40,
              padding: "0 12px 0 10px",
              borderRadius: 10,
              background: allHere ? C.selGold : C.hairRow,
              border: `1px solid ${allHere ? C.selGoldEdge : C.line}`,
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            <span
              aria-hidden
              style={{
                display: "grid",
                placeItems: "center",
                width: 22,
                height: 22,
                borderRadius: 6,
                flex: "none",
                background: allHere || someHere ? C.ink : C.surface,
                border: `1.5px solid ${allHere || someHere ? C.ink : C.selBoxEdge}`,
              }}
            >
              {allHere ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M4 12.5l5 5L20 6"
                    stroke={C.selBarOn}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : someHere ? (
                <span
                  style={{ width: 10, height: 2, borderRadius: 1, background: C.selBarOn }}
                />
              ) : null}
            </span>
            <span style={{ fontSize: 14.95, fontWeight: 600, color: C.selLabel }}>
              {allHere
                ? `All ${day.photos.length} selected`
                : someHere
                  ? `${pickedHere} of ${day.photos.length}`
                  : `Select all ${day.photos.length}`}
            </span>
          </button>
        )}

        {/* The walks that made the day, as the way into each. Derived from
            the photos, so the header cannot name a walk this day did not
            produce. */}
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
            flex: "0 1 auto",
            minWidth: 0,
          }}
        >
          {walks.map((walk) => (
            <a
              key={walk.visitId}
              className="kb"
              data-testid="photo-day-walk"
              href={hrefForWalk(walk.visitId)}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                event.preventDefault();
                onNavigate(hrefForWalk(walk.visitId));
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                height: 32,
                padding: "0 10px 0 9px",
                borderRadius: 9,
                border: `1px solid ${C.line}`,
                background: C.surface,
                fontSize: 14.37,
                fontWeight: 600,
                whiteSpace: "nowrap",
                maxWidth: 220,
                color: "inherit",
              }}
            >
              <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, fontWeight: 500, color: C.sub2 }}>
                {clockOf(walk.at)}
              </span>
              <span style={{ ...ROW_TITLE }}>{walk.name}</span>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M5 12h13M12 6l6 6-6 6"
                  stroke={C.sub2}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          ))}
        </span>
      </div>

      <div
        style={{
          padding: "14px 20px 20px",
          background: C.inset,
          display: "grid",
          gridTemplateColumns: `repeat(auto-fill, minmax(${TILE_MIN}px, 1fr))`,
          gap: TILE_GAP,
        }}
      >
        {day.photos.map((photo) => (
          <PhotoTile
            key={photoKey(photo)}
            photo={photo}
            selectMode={selectMode}
            selected={selected.has(photoKey(photo))}
            dimmed={selectMode && anySelected && !selected.has(photoKey(photo))}
            onView={() => (selectMode ? onToggleOne(photo) : onView(photo))}
          />
        ))}
      </div>
    </div>
  );
}

/** `2026-09-06` → `Sep 6, 2026`, read as a LOCAL day. */
function dayDate(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return y && m && d ? dateWithYear(new Date(y, m - 1, d)) : day;
}

/** `2026-09-06` → the three pieces the date block prints. */
function dayParts(day: string): { month: string; dayNum: string; weekday: string } {
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return { month: "", dayNum: day, weekday: day };
  const at = new Date(y, m - 1, d);
  const today = new Date();
  const midnight = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const ago = Math.round((midnight(today) - midnight(at)) / 86_400_000);
  return {
    month: MONTHS[at.getMonth()] ?? "",
    dayNum: String(at.getDate()),
    // "Today" and "Yesterday" beat a weekday for the two days a reader is
    // most often looking at, and the date block beside it still says which
    // date they are.
    weekday: ago === 0 ? "Today" : ago === 1 ? "Yesterday" : WEEKDAY_FULL[at.getDay()] ?? "",
  };
}

const WEEKDAY_FULL = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/** The day's walks, earliest first, each with the time its first photo was
 *  taken. One entry per visit — a walk with forty photos is still one walk. */
function walksOf(
  photos: ConsolePhotoResponse[],
): Array<{ visitId: string; name: string; at: string }> {
  const byVisit = new Map<string, { visitId: string; name: string; at: string }>();
  for (const photo of photos) {
    if (!photo.visitId) continue;
    const found = byVisit.get(photo.visitId);
    if (!found) {
      byVisit.set(photo.visitId, {
        visitId: photo.visitId,
        name: photo.walkName || "Untitled walk",
        at: photo.capturedAt,
      });
    } else if (Date.parse(photo.capturedAt) < Date.parse(found.at)) {
      found.at = photo.capturedAt;
    }
  }
  return [...byVisit.values()].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

/**
 * One photograph, filling its cell.
 *
 * The caption is INSIDE the tile now, not under it. Outside, every tile in
 * the row grew to the tallest caption in it, which is what made the old grid
 * a table of text with pictures attached; inside, the wall is photographs and
 * the words are on the ones that have any. It only appears on hover or focus
 * — a caption burnt into every tile is a wall of dark bars, and the point of
 * the mosaic is the pictures.
 */
function PhotoTile({
  photo,
  selectMode,
  selected,
  dimmed,
  onView,
}: {
  photo: ConsolePhotoResponse;
  selectMode: boolean;
  selected: boolean;
  /** Picked something, but not this. See the `opacity` below. */
  dimmed: boolean;
  onView: () => void;
}) {
  const src = photo.thumbnailUrl ?? photo.url;
  const caption = photo.description ?? photo.walkName ?? "";
  return (
    <button
      type="button"
      className="kb phototile"
      data-testid="photo-tile"
      data-selected={selected ? "true" : undefined}
      aria-label={caption || "Photo"}
      // In selection mode the tile IS a checkbox — `aria-pressed` rather
      // than a role swap, because it is still the same button doing a
      // different thing, and a screen reader that announced "checkbox" over
      // a control that opens a photograph the rest of the time would be
      // describing two widgets that do not exist.
      aria-pressed={selectMode ? selected : undefined}
      onClick={onView}
      style={{
        position: "relative",
        display: "block",
        padding: 0,
        aspectRatio: "4 / 3",
        borderRadius: 10,
        overflow: "hidden",
        border: `1px solid ${C.hair}`,
        background: C.faint,
        cursor: "pointer",
        minWidth: 0,
        // A ring rather than a border: a border would resize the tile and
        // reflow the row it sits in every time something was ticked.
        boxShadow: selected ? `0 0 0 3px ${C.gold}` : "none",
        // Only once something IS picked. Dimming every tile the moment the
        // mode opens would say the whole wall had been deselected, when
        // nothing has been decided yet.
        opacity: dimmed ? 0.72 : 1,
      }}
    >
      {src && (
        <PhotoImage
          src={src}
          alt=""
          // 60 photos on screen is 60 requests without this — see
          // `EvidenceTile`, which lazies for the same reason.
          loading="lazy"
          decoding="async"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      )}

      {selectMode && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "grid",
            placeItems: "center",
            width: 22,
            height: 22,
            borderRadius: 6,
            background: selected ? C.gold : C.selBoxBg,
            border: `1.5px solid ${selected ? C.gold : C.selBoxEdge}`,
            // The box sits on a photograph, which can be any colour. The
            // shadow is what keeps it from disappearing into a pale one.
            boxShadow: `0 1px 3px ${C.selBoxShadow}`,
          }}
        >
          {selected && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 12.5l5 5L20 6"
                stroke={C.onGold}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
      )}

      <span
        style={{
          position: "absolute",
          left: 8,
          top: 8,
          fontFamily: FONT_MONO,
          fontSize: 10.35,
          fontWeight: 700,
          letterSpacing: "0.7px",
          color: C.surface,
          background: "rgba(10,10,10,0.62)",
          padding: "3px 7px",
          borderRadius: 5,
        }}
      >
        {clockOf(photo.capturedAt)}
      </span>

      {caption && (
        <span
          className="phototile-cap"
          style={{
            position: "absolute",
            left: 8,
            right: 8,
            bottom: 8,
            display: "block",
            textAlign: "left",
            padding: "5px 9px",
            borderRadius: 999,
            background: "rgba(10,10,10,0.78)",
            color: C.surface,
            fontSize: 13.23,
            fontWeight: 500,
            lineHeight: 1.3,
            ...ROW_TITLE,
          }}
        >
          {caption}
        </span>
      )}
    </button>
  );
}

/** `7:14 AM`. The tile's own stamp — spelled out rather than the console's
 *  list voice (`7:14a`), because on a photo the meridiem is the only thing
 *  separating two frames taken twelve hours apart. */
function clockOf(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const hours = at.getHours();
  return `${hours % 12 || 12}:${String(at.getMinutes()).padStart(2, "0")} ${
    hours < 12 ? "AM" : "PM"
  }`;
}
